// First-party GTM / GA4 / Clarity / Plausible on app.funnelkeeper.com.
// Unset env = no-op so local/dev never phones home. Events always go to
// dataLayer (GTM) and, when present, gtag + Clarity + Plausible. Don't also
// add GA4 or Clarity as GTM tags if VITE_GA4_MEASUREMENT_ID /
// VITE_CLARITY_PROJECT_ID are set — you'd double-count.

import type { Route } from "../router";
import { getSession } from "../session";
import { installFk, fkIdentify, fkTrack } from "./fk";
import { installMetaPixel, trackMeta } from "./meta";

const FK_STAGES: Record<string, string> = {
  generate_lead: "lead",
  sign_up: "signup",
  onboarding_complete: "activated",
  source_connected: "activated",
  // paid_subscription is server-side only (Stripe webhook has the amount +
  // a stable session id). The dataLayer event still fires for GTM / Ads.
};

type Props = Record<string, string | number | boolean | undefined>;

type PlausibleFn = ((...args: unknown[]) => void) & {
  q?: unknown[];
  init?: (opts?: Record<string, unknown>) => void;
  o?: Record<string, unknown>;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    plausible?: PlausibleFn;
  }
}

const GTM_ID = (import.meta.env.VITE_GTM_ID ?? "").trim();
const GA4_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID ?? "").trim();
const CLARITY_ID = (import.meta.env.VITE_CLARITY_PROJECT_ID ?? "").trim();
const PLAUSIBLE_ID = (import.meta.env.VITE_PLAUSIBLE_SCRIPT ?? "").trim();

const gtmId = /^GTM-[A-Z0-9]+$/i.test(GTM_ID) ? GTM_ID : "";
const ga4Id = /^G-[A-Z0-9]+$/i.test(GA4_ID) ? GA4_ID : "";
const clarityId = /^[a-z0-9]+$/i.test(CLARITY_ID) ? CLARITY_ID : "";
const plausibleId = /^[A-Za-z0-9_-]+$/.test(PLAUSIBLE_ID) ? PLAUSIBLE_ID : "";

function dataLayer(): unknown[] {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

function inject(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

export function installAnalytics(): void {
  if (typeof window === "undefined") return;
  installFk();
  installMetaPixel();

  if (gtmId) {
    dataLayer().push({ "gtm.start": Date.now(), event: "gtm.js" });
    inject(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
  }

  if (ga4Id) {
    window.gtag = function gtag() {
      // gtag reads the Arguments object, not a real array.
      dataLayer().push(arguments);
    };
    window.gtag("js", new Date());
    // SPA: we send page_view on hash changes ourselves.
    window.gtag("config", ga4Id, { send_page_view: false });
    inject(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`);
  }

  if (clarityId) {
    type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };
    const queued: ClarityFn = (...args: unknown[]) => {
      queued.q = queued.q ?? [];
      queued.q.push(args);
    };
    window.clarity = window.clarity ?? queued;
    inject(`https://www.clarity.ms/tag/${encodeURIComponent(clarityId)}`);
  }

  if (plausibleId) {
    const queued: PlausibleFn = (...args: unknown[]) => {
      queued.q = queued.q ?? [];
      queued.q.push(args);
    };
    queued.init = (opts) => {
      queued.o = opts ?? {};
    };
    window.plausible = window.plausible ?? queued;
    // Hash routes carry invite/reset tokens — we send sanitized pageviews
    // ourselves instead of letting the script read location.hash.
    window.plausible.init?.({ autoCapturePageviews: false });
    inject(`https://plausible.io/js/pa-${encodeURIComponent(plausibleId)}.js`);
  }
}

export function track(event: string, props: Props = {}): void {
  if (typeof window === "undefined" || !event) return;
  const clean = Object.fromEntries(
    Object.entries(props).filter(([, v]) => v !== undefined),
  );
  dataLayer().push({ event, ...clean });
  window.gtag?.("event", event, clean);
  window.clarity?.("event", event);
  const plausibleProps = Object.fromEntries(
    Object.entries(clean).map(([k, v]) => [k, String(v)]),
  );
  if (Object.keys(plausibleProps).length) window.plausible?.(event, { props: plausibleProps });
  else window.plausible?.(event);
  if (event === "generate_lead") trackMeta("Lead", clean);
  if (event === "sign_up") {
    trackMeta(
      "CompleteRegistration",
      clean,
      typeof clean.event_id === "string" ? clean.event_id : undefined,
    );
  }
  const stage = FK_STAGES[event];
  if (stage) {
    const email = (typeof clean.email === "string" && clean.email)
      || getSession()?.account.email;
    const dedupe = typeof clean.dedupe_id === "string" && clean.dedupe_id
      ? clean.dedupe_id
      : stage === "signup" && email ? `signup:${email.trim().toLowerCase()}`
      : stage === "activated" && email ? `activated:${email.trim().toLowerCase()}`
      : undefined;
    fkTrack(stage, {
      name: event,
      email,
      dedupe_id: dedupe,
      value_cents: typeof clean.value_cents === "number" ? clean.value_cents : undefined,
      currency: typeof clean.currency === "string" ? clean.currency : undefined,
      props: clean,
    });
  }
  for (const [k, v] of Object.entries(clean)) {
    if (typeof v === "string" || typeof v === "number") window.clarity?.("set", k, String(v));
  }
}

export function identify(who: { userId?: string; email?: string }): void {
  if (typeof window === "undefined" || !who.userId) return;
  // Account id only — don't send email into Clarity/GA4 user_id.
  if (ga4Id) window.gtag?.("config", ga4Id, { user_id: who.userId, send_page_view: false });
  window.clarity?.("identify", who.userId);
  fkIdentify({ email: who.email, external_id: who.userId });
}

export function pageview(path: string, title: string): void {
  if (typeof window === "undefined") return;
  const page_location = `${location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  const page_path = path.startsWith("/") ? path : `/${path}`;
  dataLayer().push({ event: "page_view", page_path, page_title: title, page_location });
  if (ga4Id) window.gtag?.("event", "page_view", { page_path, page_title: title, page_location });
  window.plausible?.("pageview", { url: page_location, props: { title } });
  trackMeta("PageView", { page_path, page_title: title });
}

/** Hash routes that carry secrets become a stable path with the token stripped. */
export function pathForRoute(route: Route): string {
  switch (route.name) {
    case "product":
      return route.slug ? `/#/p/${route.slug}/${route.section}` : "/#/";
    case "verify":
      return "/#/verify";
    case "invite":
      return "/#/invite";
    case "reset":
      return "/#/reset";
    case "google":
      return "/#/auth/google";
    case "connected":
      return "/#/connected";
    case "scorecardShare":
      return "/#/scorecard";
    case "integrations":
      return route.kind ? `/#/integrations/${route.kind}` : "/#/integrations";
    case "settings":
      return route.tab && route.tab !== "general" ? `/#/settings/${route.tab}` : "/#/settings";
    case "admin":
      if (route.id) return "/#/admin/a";
      if (route.tab === "users") return "/#/admin/users";
      if (route.tab === "accounts") return "/#/admin/accounts";
      return "/#/admin";
    default:
      return `/#/${route.name}`;
  }
}

export function titleForRoute(route: Route): string {
  switch (route.name) {
    case "login":
      return "Sign in";
    case "signup":
      return "Create account";
    case "verify":
      return "Verify email";
    case "invite":
      return "Accept invite";
    case "reset":
      return "Reset password";
    case "google":
      return "Google sign-in";
    case "connected":
      return "Google connected";
    case "scorecardShare":
      return "Weekly scorecard";
    case "integrations":
      return "Integrations";
    case "settings":
      if (route.tab === "team") return "Settings · Team";
      if (route.tab === "billing") return "Settings · Billing";
      if (route.tab === "pipeline") return "Settings · Pipeline";
      return "Settings";
    case "admin":
      return "Admin";
    case "product":
      return route.section[0].toUpperCase() + route.section.slice(1);
    default:
      return "FunnelKeeper";
  }
}
